import { Capacitor } from '@capacitor/core';
import { get, ref, serverTimestamp, update } from 'firebase/database';
import { db } from './app';

/**
 * Les documents legaux, leurs versions, et la preuve du consentement.
 *
 * # Ce qu'on cherche a prouver
 *
 * Le RGPD demande de pouvoir montrer *quand* et *a quoi* une personne a
 * consenti. Un drapeau « a accepte » ne suffit pas : il faut la version
 * du texte accepte, et une trace qui ne se reecrit pas. D'ou deux
 * branches : `current`, qui dit l'etat, et `history`, ou l'on ajoute
 * sans jamais modifier — les regles de la base l'interdisent a tous, y
 * compris au proprietaire du compte.
 *
 * # La comparaison se fait par egalite de chaine
 *
 * Jamais par ordre. « 1.10 » n'est pas « plus grand » que « 1.9 » dans
 * cette application : il est simplement *different*, donc il faut
 * reconsentir. Comparer des numeros de version dans l'ordre semantique
 * ouvre la porte a un retour en arriere qui passerait inapercu.
 *
 * # Personne n'est enferme dehors
 *
 * C'est la regle qui prime sur toutes les autres. Si la configuration
 * est injoignable et qu'aucun cache n'existe, on laisse passer. Un
 * ecran de consentement qu'on ne peut pas franchir parce que le reseau
 * est mauvais transforme une precaution juridique en panne totale.
 */

/* --- Formes ---------------------------------------------------------- */

export interface DocumentLegal {
  version: string;
  url: string;
  updatedAt?: number;
  /** Un changement de fond, qui redemande le consentement. */
  requiresReconsent?: boolean;
}

export interface ConfigLegale {
  cgu: DocumentLegal;
  privacy: DocumentLegal;
  /** Les mentions legales informent ; elles ne s'acceptent pas. */
  legalNotice: DocumentLegal;
}

export interface Consentement {
  cguVersion: string;
  privacyVersion: string;
  acceptedAt: number;
  platform?: string;
  appVersion?: string;
}

/** Quel document a change, pour ne lister que celui-la a l'ecran. */
export type DocumentAConsentir = 'cgu' | 'privacy';

export type BesoinDeConsentement =
  | { type: 'aucun' }
  | { type: 'initial' }
  | { type: 'reconsentement'; documents: DocumentAConsentir[] };

/* --- Reglages -------------------------------------------------------- */

/**
 * Au-dela, on considere la configuration injoignable.
 *
 * Trois secondes : au lancement de l'application, c'est deja long a
 * regarder un ecran vide, et c'est trois fois le temps d'une lecture
 * normale. Ce qui depasse est un reseau qui ne repond pas.
 */
const DELAI_MAX_MS = 3000;

const CLE_CACHE = 'sirocco.legal.config';
const CLE_ATTENTE = 'sirocco.legal.aRejouer';

/* --- Cache local ----------------------------------------------------- */

function lireLeCache(): ConfigLegale | null {
  try {
    const brut = localStorage.getItem(CLE_CACHE);
    return brut ? (JSON.parse(brut) as ConfigLegale) : null;
  } catch {
    return null;
  }
}

function ecrireLeCache(config: ConfigLegale): void {
  try {
    localStorage.setItem(CLE_CACHE, JSON.stringify(config));
  } catch {
    /* Un stockage plein ou refuse ne doit pas empecher l'application de tourner. */
  }
}

/* --- Lecture de la configuration ------------------------------------- */

function complet(v: unknown): v is DocumentLegal {
  const d = v as DocumentLegal | null;
  return Boolean(d && typeof d.version === 'string' && typeof d.url === 'string');
}

/**
 * La configuration en vigueur, ou le cache, ou rien.
 *
 * `rien` n'est pas une erreur : c'est le cas « on ne sait pas », et
 * l'appelant doit alors laisser passer.
 */
export async function fetchLegalConfig(): Promise<ConfigLegale | null> {
  try {
    const lecture = get(ref(db(), 'config/legal'));
    const limite = new Promise<never>((_, rejeter) =>
      setTimeout(() => rejeter(new Error('delai depasse')), DELAI_MAX_MS),
    );
    const instantane = await Promise.race([lecture, limite]);
    const valeur = instantane.val() as Partial<ConfigLegale> | null;

    if (!valeur || !complet(valeur.cgu) || !complet(valeur.privacy)) {
      /*
        Une configuration incomplete vaut une absence : mieux vaut le
        cache d'hier, dont on sait qu'il tenait debout.
      */
      return lireLeCache();
    }

    const config = valeur as ConfigLegale;
    ecrireLeCache(config);
    return config;
  } catch {
    return lireLeCache();
  }
}

/* --- Le consentement en place ---------------------------------------- */

export async function lireLeConsentement(uid: string): Promise<Consentement | null> {
  try {
    const instantane = await get(ref(db(), `consents/${uid}/current`));
    return (instantane.val() as Consentement | null) ?? null;
  } catch {
    return null;
  }
}

/* --- La decision ----------------------------------------------------- */

/**
 * Faut-il demander quelque chose, et quoi.
 *
 * Fonction pure : elle ne lit rien, on lui donne l'etat. C'est ce qui la
 * rend testable exhaustivement, et c'est la seule piece dont une erreur
 * enfermerait quelqu'un dehors.
 */
export function needsConsent(
  config: ConfigLegale | null,
  consentement: Consentement | null,
): BesoinDeConsentement {
  // Configuration inconnue : on ne bloque pas. La regle qui prime.
  if (!config) return { type: 'aucun' };

  // Jamais rien accepte : c'est une premiere, pas un reconsentement.
  if (!consentement) return { type: 'initial' };

  const documents: DocumentAConsentir[] = [];

  /*
    Un document ne redemande le consentement que s'il a change **et**
    que le changement le demande. Une correction de virgule publie une
    version nouvelle sans dresser un mur devant chaque membre.
  */
  if (
    consentement.cguVersion !== config.cgu.version &&
    config.cgu.requiresReconsent !== false
  ) {
    documents.push('cgu');
  }
  if (
    consentement.privacyVersion !== config.privacy.version &&
    config.privacy.requiresReconsent !== false
  ) {
    documents.push('privacy');
  }

  return documents.length > 0 ? { type: 'reconsentement', documents } : { type: 'aucun' };
}

/* --- Enregistrer le consentement ------------------------------------- */

/**
 * La cle d'une entree d'historique : la paire de versions acceptees.
 *
 * Deterministe, et c'est tout l'interet. Un double appui, ou un rejeu
 * apres une coupure, retombe sur la meme cle — donc sur la meme entree,
 * et non sur une seconde. Les regles refusant l'ecrasement, la
 * duplication devient impossible cote serveur aussi, et pas seulement
 * par politesse du client.
 */
function cleHistorique(cgu: string, privacy: string): string {
  return `${cgu}__${privacy}`.replace(/[.#$/[\]]/g, '-');
}

function contexte(): { platform: string; appVersion: string } {
  return {
    platform: Capacitor.getPlatform(),
    appVersion: typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'dev',
  };
}

/** Ce qu'on garde sous le coude quand l'ecriture n'a pas abouti. */
interface EnAttente {
  uid: string;
  cguVersion: string;
  privacyVersion: string;
}

function poserEnAttente(entree: EnAttente): void {
  try {
    localStorage.setItem(CLE_ATTENTE, JSON.stringify(entree));
  } catch {
    /* Rien de mieux a faire : le prochain consentement reecrira. */
  }
}

function retirerDeLAttente(): void {
  try {
    localStorage.removeItem(CLE_ATTENTE);
  } catch {
    /* sans consequence */
  }
}

/** Deux appuis rapprochés ne doivent pas partir deux fois. */
let enCours: Promise<boolean> | null = null;

/**
 * Ecrit l'etat et la preuve, en une seule passe.
 *
 * Une mise a jour groupee est tout ou rien : soit les deux chemins sont
 * ecrits, soit aucun. On ne peut donc pas se retrouver avec un `current`
 * a jour sans la trace qui le justifie.
 *
 * Rend `false` si l'ecriture n'a pas abouti — l'appelant laisse alors
 * passer quand meme, et le rejeu s'en chargera.
 */
export async function recordConsent(
  uid: string,
  config: ConfigLegale,
): Promise<boolean> {
  if (enCours) return enCours;

  enCours = (async () => {
    const cguVersion = config.cgu.version;
    const privacyVersion = config.privacy.version;
    const { platform, appVersion } = contexte();

    const entree = {
      cguVersion,
      privacyVersion,
      acceptedAt: serverTimestamp(),
      platform,
      appVersion,
    };

    const cle = cleHistorique(cguVersion, privacyVersion);
    try {
      await update(ref(db()), {
        [`consents/${uid}/current`]: entree,
        [`consents/${uid}/history/${cle}`]: entree,
      });
      retirerDeLAttente();
      return true;
    } catch {
      /*
        L'echec le plus probable n'est pas le reseau mais la regle qui
        refuse de reecrire une entree d'historique deja posee — un rejeu,
        donc. Dans ce cas la preuve existe deja et il ne reste qu'a
        remettre l'etat courant.
      */
      try {
        await update(ref(db()), { [`consents/${uid}/current`]: entree });
        retirerDeLAttente();
        return true;
      } catch {
        poserEnAttente({ uid, cguVersion, privacyVersion });
        return false;
      }
    }
  })().finally(() => {
    enCours = null;
  });

  return enCours;
}

/**
 * Rejouer un consentement que le reseau avait emporte.
 *
 * A appeler au retour au premier plan et a la reconnexion. La cle etant
 * deterministe, rejouer deux fois ne produit pas deux traces.
 */
export async function rejouerLeConsentement(uid: string): Promise<void> {
  let attente: EnAttente | null = null;
  try {
    const brut = localStorage.getItem(CLE_ATTENTE);
    attente = brut ? (JSON.parse(brut) as EnAttente) : null;
  } catch {
    return;
  }
  if (!attente || attente.uid !== uid) return;

  const config = lireLeCache();
  if (!config) return;
  // Les versions d'alors, pas celles d'aujourd'hui : on rejoue ce qui a
  // ete accepte, pas ce qui est en vigueur depuis.
  await recordConsent(uid, {
    ...config,
    cgu: { ...config.cgu, version: attente.cguVersion },
    privacy: { ...config.privacy, version: attente.privacyVersion },
  });
}
